export type Role = "platform_admin" | "tenant_admin" | "supervisor" | "scanner";

export interface Session {
  email: string;
  displayName: string;
  role: Role;
  tenantId: string | null;
}

export interface Inspection {
  id: string;
  decision: string;
  admittedCount: number;
  remainingCount: number | null;
  capacity: number | null;
  vendorTicketRef: string | null;
  codeMask: string | null;
  detail: { message?: string; operatorAction?: string; limitations?: string[]; cancelled?: boolean };
  createdAt: string | null;
  entranceName?: string | null;
}

let experienceToken = "";

export function setExperienceToken(token: string) {
  experienceToken = token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  headers.set("x-mv-request", "1");
  if (experienceToken) headers.set("authorization", `Bearer ${experienceToken}`);
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.error?.message ?? "요청에 실패했습니다.");
  }
  return body as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<Session>("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => request("/api/v1/auth/logout", { method: "POST", body: "{}" }),
  me: () => request<Session>("/api/v1/me"),
  context: (tenantId?: string) =>
    request<{
      tenant: { id: string; name: string; code: string; force_simulation: boolean; features: Record<string, boolean>; license_end: string; max_devices: number };
      entrances: Array<{ id: string; name: string; code: string; vendor_code: string; issuer_name: string; adapter_code: string | null; mode: string | null }>;
      actor: Session;
      adapters: Array<{ vendorCode: string; duplicatePrevention: string; limitations: string[]; atomicConsume: boolean; supportsPartial: boolean; supportsCancel: boolean }>;
    }>("/api/v1/context", { headers: tenantId ? { "x-tenant-id": tenantId } : {} }),
  inspect: (payload: { requestId: string; entranceId: string; code: string; admitCount: number; admitMode: "partial" | "all" }) =>
    request<Inspection>("/api/v1/inspections", { method: "POST", body: JSON.stringify(payload) }),
  reconcile: (id: string, code: string) =>
    request<Inspection>(`/api/v1/inspections/${id}/reconcile`, { method: "POST", body: JSON.stringify({ code }) }),
  cancel: (id: string) => request<Inspection>(`/api/v1/inspections/${id}/cancel`, { method: "POST", body: "{}" }),
  history: () => request<{ items: Inspection[] }>("/api/v1/inspections?limit=200"),
  status: () => request<{ issuers: Array<{ id: string; name: string; ok: boolean; latencyMs: number }>; gates: Array<{ name: string; mode: string; adapter_code: string; last_heartbeat_at: string | null }> }>("/api/v1/status"),
  stats: () => request<{ items: Array<{ decision: string; count: number; admitted: number }> }>("/api/v1/stats"),
  gates: () => request<{ items: Array<{ entrance_id: string; entrance_name: string; mode: string; adapter_code: string }> }>("/api/v1/gates"),
  commands: () => request<{ items: Array<{ id: string; status: string; kind: string; expires_at: string; result_detail: { message?: string } }> }>("/api/v1/gates/commands"),
  openGate: (entranceId: string) => request(`/api/v1/gates/${entranceId}/open`, { method: "POST", body: "{}" }),
  staff: () => request<{ items: Array<{ email: string; display_name: string; role: string }> }>("/api/v1/staff"),
  createStaff: (body: { email: string; displayName: string; password: string; role: string }) =>
    request("/api/v1/staff", { method: "POST", body: JSON.stringify(body) }),
  tenants: () => request<{ items: Array<{ id: string; code: string; name: string; license_end: string; max_devices: number; status: string }> }>("/api/v1/platform/tenants"),
  updateTenant: (id: string, body: Record<string, unknown>) =>
    request(`/api/v1/platform/tenants/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  demoTickets: () => request<{ tickets: Array<{ code: string; label: string; vendor: string }> }>("/api/v1/demo/tickets"),
  startExperience: () => request<{ token: string; expiresIn: number; experienceId: string }>("/api/v1/experience/start", { method: "POST", body: "{}" }),
  resetExperience: () => request<{ ok: boolean }>("/api/v1/experience/reset", { method: "POST", body: "{}" }),
  issueSample: (kind: "valid" | "again" | "cancel" | "period" | "unknown") =>
    request<{ code: string; fact: string; kind: string }>("/api/v1/experience/sample", { method: "POST", body: JSON.stringify({ kind }) }),
};
